/**
 * Collapsible Component
 *
 * An interactive component which expands/collapses a panel.
 * Built on Radix UI Collapsible primitive.
 *
 * @example
 * ```tsx
 * <Collapsible>
 *   <CollapsibleTrigger>Toggle</CollapsibleTrigger>
 *   <CollapsibleContent>Collapsible content</CollapsibleContent>
 * </Collapsible>
 * ```
 */

'use client';

import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';

// =============================================================================
// COLLAPSIBLE ROOT
// =============================================================================

const Collapsible = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>
>(({ ...props }, ref) => (
  <CollapsiblePrimitive.Root ref={ref} data-slot="collapsible" {...props} />
));
Collapsible.displayName = 'Collapsible';

// =============================================================================
// COLLAPSIBLE TRIGGER
// =============================================================================

const CollapsibleTrigger = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleTrigger>
>(({ ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleTrigger
    ref={ref}
    data-slot="collapsible-trigger"
    {...props}
  />
));
CollapsibleTrigger.displayName = 'CollapsibleTrigger';

// =============================================================================
// COLLAPSIBLE CONTENT
// =============================================================================

const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleContent
    ref={ref}
    data-slot="collapsible-content"
    {...props}
  />
));
CollapsibleContent.displayName = 'CollapsibleContent';

// =============================================================================
// EXPORTS
// =============================================================================

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
